"use client"

import { useActionState, useEffect } from "react"
import { toast } from "sonner"
import { DZTypes } from "@dealerzone/libs"
import SubmitButton from "@/components/SubmitButton"
import { Turnstile } from "@dealerzone/libs/client"
import { TURNSTILE_SITE_KEY } from "@/lib/turnstileSiteKey"

export type WaitingListFormState = {
  response: DZTypes.SubmissionResponse
  email: string
  attempt: number
} | null

interface WaitingListFormProps {
  action: (prevState: WaitingListFormState, formData: FormData) => Promise<WaitingListFormState>
}

export default function WaitingListForm({ action }: WaitingListFormProps) {
  const [state, formAction] = useActionState(action, null)
  const errors = state?.response.errors
  const submitted = state?.response.success === true

  useEffect(() => {
    if (!state || state.response.success) {
      return
    }
    const errorMessage = state.response.errors
      ? Object.values(state.response.errors).flat().join(", ")
      : state.response.message || "Failed to join waiting list"
    toast.error(errorMessage)
  }, [state])

  if (submitted) {
    return (
      <div className="mt-4 rounded-lg bg-green-100 border border-green-300 px-4 py-3">
        <p className="text-sm font-medium text-green-800">
          <i className="tabler-circle-check-filled mr-1.5 align-middle"></i>
          You&apos;ve been added to the waiting list. We&apos;ll email you if this vehicle becomes available.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-4">
      <p className="text-sm font-medium text-orange-700">
        Interested? Join the waiting list and we&apos;ll notify you if this vehicle goes back up for sale.
      </p>
      <form action={formAction} className="mt-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            name="email"
            defaultValue={state?.email}
            placeholder="you@example.com"
            className={`flex-1 rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 ${errors?.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-orange-300 focus:border-amber-500 focus:ring-amber-500"}`}
          />
          <SubmitButton
            label="Join Waiting List"
            pendingLabel="Joining..."
            className="rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
          />
        </div>
        <div className="mt-3">
          <Turnstile key={state?.attempt ?? 0} siteKey={TURNSTILE_SITE_KEY} />
        </div>
      </form>
    </div>
  )
}
